import { c as _c } from "react/compiler-runtime";
import { trackEvent } from '@svenskaspelab/nova-core/nextjs/analytics';
import { loginRedirect } from '@svenskaspelab/nova-core/nextjs/auth';
import { getFormattedCurrency } from '@svenskaspelab/poolbetting/core/lib';
import type { CouponIndex } from '@svenskaspelab/poolbetting/schema/coupon';
import type { Draw } from '@svenskaspelab/poolbetting/schema/draw';
import type { BettingEvent, BettingEventId, EventType, Participant, ParticipantId } from '@svenskaspelab/poolbetting/schema/sport';
import { ShareButton } from '@svenskaspelab/poolbetting/ui/ShareButton';
import { ShowHideButton } from '@svenskaspelab/poolbetting/ui/ShowHideButton';
import Balance from '@svenskaspelab/ui/react/icons/Balance';
import { useMediaQueryAbove } from '@svenskaspelab/ui/react/media-query';
import type { ComponentProps, ComponentType, ReactNode } from 'react';
import type CouponOverviewRowContainer from './CouponOverviewRowContainer';
export type CouponOverviewProps = {
  couponIndex: CouponIndex;
  draw: Draw;
  drawParticipants: Record<ParticipantId, Participant>;
  bettingEvents: Record<BettingEventId, BettingEvent>;
  isDisabled: boolean;
  url: string;
  balance?: number;
  price: number;
  fullHedgeCount: number;
  halfHedgeCount: number;
  defaultEventType: EventType;
  shouldRedirectUser: boolean;
  hasMultipleCoupons?: boolean;
  CouponOverviewRow: ComponentType<ComponentProps<typeof CouponOverviewRowContainer>>;
};

/**
 * CouponOverview component.
 *
 * @param root0 - The props object.
 * @param root0.couponIndex - The index of the current coupon
 * @param root0.draw the current draw
 * @param root0.drawParticipants - The participants of the current draw
 * @param root0.bettingEvents - The bet events of the current draw
 * @param root0.isDisabled - The status of the current coupon
 * @param root0.url - The url to share
 * @param root0.balance - The balance to display
 * @param root0.price - Numeric value representing the total cost of the coupon
 * @param root0.fullHedgeCount - The number of full hedges
 * @param root0.halfHedgeCount - The number of half hedges
 * @param root0.defaultEventType - Fallback event type
 * @param root0.shouldRedirectUser - Flag indicating whether the user should be redirected to the login page when clicking on the balance
 * @param root0.CouponOverviewRow - The container component for each coupon row
 * @param root0.hasMultipleCoupons - Flag indicating whether multiple coupons are available, which can affect the UI rendering
 * @returns The coupon overview component.
 */
export default function CouponOverview(t0) {
  const $ = _c(51);
  const {
    couponIndex,
    draw,
    drawParticipants,
    isDisabled,
    url,
    bettingEvents,
    balance: t1,
    price,
    fullHedgeCount,
    halfHedgeCount,
    defaultEventType,
    shouldRedirectUser,
    CouponOverviewRow,
    hasMultipleCoupons
  } = t0;
  const balance = t1 === undefined ? 0 : t1;
  const isLargeScreen = useMediaQueryAbove("md");
  const handleBalanceClick = shouldRedirectUser ? _temp : undefined;
  const t2 = `border-stroke-low rounded-md border p-2 ${hasMultipleCoupons ? "mt-2" : "mx-2 my-4 lg:m-4"}`;
  let t3;
  if ($[0] !== CouponOverviewRow || $[1] !== bettingEvents || $[2] !== couponIndex || $[3] !== defaultEventType || $[4] !== draw.events || $[5] !== draw.sportType || $[6] !== drawParticipants) {
    let t4;
    if ($[8] !== CouponOverviewRow || $[9] !== bettingEvents || $[10] !== couponIndex || $[11] !== defaultEventType || $[12] !== draw.sportType || $[13] !== drawParticipants) {
      t4 = drawEvent => {
        const eventParticipants = drawEvent.status === "COMPLETE" ? bettingEvents[drawEvent.bettingEventId]?.participants.map(participantId => drawParticipants[participantId]) : undefined;
        const eventType = drawEvent.status === "COMPLETE" ? bettingEvents[drawEvent.bettingEventId]?.eventType ?? defaultEventType : defaultEventType;
        return <CouponOverviewRow couponIndex={couponIndex} eventParticipants={eventParticipants} eventNumber={drawEvent.eventNumber} eventDescription={drawEvent.eventDescription} eventType={eventType} sportType={draw.sportType} key={drawEvent.eventNumber} />;
      };
      $[8] = CouponOverviewRow;
      $[9] = bettingEvents;
      $[10] = couponIndex;
      $[11] = defaultEventType;
      $[12] = draw.sportType;
      $[13] = drawParticipants;
      $[14] = t4;
    } else {
      t4 = $[14];
    }
    t3 = draw.events.map(t4);
    $[0] = CouponOverviewRow;
    $[1] = bettingEvents;
    $[2] = couponIndex;
    $[3] = defaultEventType;
    $[4] = draw.events;
    $[5] = draw.sportType;
    $[6] = drawParticipants;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[15] !== t2 || $[16] !== t3) {
    t4 = <div className={t2}>{t3}</div>;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const t5 = `${hasMultipleCoupons ? "" : "pr-6 pl-6"} mt-3 hidden grid-flow-col grid-cols-[max-content_max-content_max-content_max-content_auto] gap-x-3 text-sm lg:grid`;
  let t6;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div>M-system</div>;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== fullHedgeCount) {
    t7 = <div>Hel: <span className="font-normal">{fullHedgeCount}</span></div>;
    $[19] = fullHedgeCount;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div>|</div>;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== halfHedgeCount) {
    t9 = <div>Halv: <span className="font-normal">{halfHedgeCount}</span></div>;
    $[22] = halfHedgeCount;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const t10 = `${price} kr`;
  let t11;
  if ($[24] !== t10) {
    t11 = <div className="justify-self-end font-medium">{t10}</div>;
    $[24] = t10;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t11 || $[27] !== t5 || $[28] !== t7 || $[29] !== t9) {
    t12 = <div className={t5}>{t6}{t7}{t8}{t9}{t11}</div>;
    $[26] = t11;
    $[27] = t5;
    $[28] = t7;
    $[29] = t9;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  const t13 = `${hasMultipleCoupons ? "" : "lg:pr-6 lg:pl-6"} mt-3 grid grid-flow-col px-4 pb-6 lg:px-0`;
  let t14;
  if ($[31] !== balance) {
    t14 = getFormattedCurrency(balance);
    $[31] = balance;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== handleBalanceClick || $[34] !== t14) {
    t15 = <ShowHideButton className="w-fit text-left lg:hidden" showValue={t14} hideValue="Visa saldo" variant="ghost" onClick={handleBalanceClick} Icon={Balance} />;
    $[33] = handleBalanceClick;
    $[34] = t14;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  const t16 = isLargeScreen ? "outline" : "ghost";
  let t17;
  if ($[36] !== url) {
    t17 = () => {
      trackEvent({
        category: "Nya tipsen",
        action: "Dela din kupong",
        optLabel: url
      });
    };
    $[36] = url;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== isDisabled || $[39] !== t16 || $[40] !== t17 || $[41] !== url) {
    t18 = <ShareButton className="w-fit justify-self-end" variant={t16} isDisabled={isDisabled} url={url} onClick={t17} />;
    $[38] = isDisabled;
    $[39] = t16;
    $[40] = t17;
    $[41] = url;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== t13 || $[44] !== t15 || $[45] !== t18) {
    t19 = <div className={t13}>{t15}{t18}</div>;
    $[43] = t13;
    $[44] = t15;
    $[45] = t18;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] !== t12 || $[48] !== t19 || $[49] !== t4) {
    t20 = <div>{t4}{t12}{t19}</div>;
    $[47] = t12;
    $[48] = t19;
    $[49] = t4;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  return t20;
}
function _temp() {
  loginRedirect();
}